import { Avatar, Button } from "@deligoo/ui"
import clsx from "clsx"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { batch, shallowEqual, useDispatch } from "react-redux"
import { toast } from "react-toastify"

import { useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { ContextMenu, openContextMenu } from "@/components/ContextMenu"
import { Panel } from "@/components/Panel"
import panelCls from "@/components/Panel/Panel.module.scss"
import { Tabs } from "@/components/Tabs"
import { useDriversContextMenuTrigger } from "@/modules/DriverContextMenu"
import { selectDriverById } from "@/store/drivers"
import {
  setAboutPanel,
  setDriverIdTasksRoute,
  setDriverIdTasksRouteHistory,
  setFocusedDriver,
  setFocusedOrder,
  setFocusedOrderArea,
  setShowDriverDetails,
  setShowOrderDetails,
} from "@/store/ui"
import { Driver } from "@/types"
import { DriverUtils } from "@/utils"

import cls from "./DriverPanel.module.scss"
import { DriverDetails, DriverHistory } from "./partials"

type DriverPanelProps = {
  driverId: Driver["id"]
}

export const DriverPanel = ({ driverId }: DriverPanelProps) => {
  const driver = useAppSelector(selectDriverById(driverId), shallowEqual)

  const [currentTab, setCurrentTab] = useState("details") //todo

  const contextMenuRef = useDriversContextMenuTrigger({ driverId })

  const dispatch = useDispatch()

  const { t } = useTranslation()

  useEffect(() => {
    return () => {
      dispatch(setDriverIdTasksRoute(null))
      dispatch(setDriverIdTasksRouteHistory(null))
    }
  }, [dispatch])

  async function handleDriverClick() {
    if (!driver) return null

    const driverUrl = `${window.location.origin}/?team_id=${driver.team_id}&driver_id=${driver.id}`

    const TOAST_OPTIONS = {
      position: "top-right",
      toastId: "copyToClipboard",
      updateId: "copyToClipboard",
      autoClose: 2000,
    } as const

    try {
      await navigator.clipboard.writeText(driverUrl)
      toast.success(t("Skopiowano link do kuriera"), TOAST_OPTIONS)
    } catch {
      toast.error(t("clipboard.fail"), TOAST_OPTIONS)
    }
  }

  if (!driver) return null

  return (
    <section ref={contextMenuRef}>
      <Panel.Header className={cls.header}>
        <div className={cls.driver} onClick={handleDriverClick}>
          <Avatar
            color={DriverUtils.getAvatarColor(driver)}
            indicator={DriverUtils.getAvatarIndicator(driver)}
            dotColor={DriverUtils.getAvatarDotColor(driver)}
          >
            {driver.name}
          </Avatar>
          <div className={cls.content}>
            <div className={cls.name}>{driver.name}</div>
            <div className={clsx(cls.state, cls[driver.state])}>{t(`drivers:${driver.state}`)}</div>
          </div>
        </div>

        <Button
          size="small"
          color="primary"
          variation="text"
          iconProps={{ name: "close" }}
          onClick={() => {
            batch(() => {
              dispatch(setAboutPanel(false))
              dispatch(setShowDriverDetails(false))
              dispatch(setShowOrderDetails(false))
              dispatch(setFocusedDriver(null))
              dispatch(setFocusedOrder(null))
              dispatch(setFocusedOrderArea(null))
            })
          }}
        />
      </Panel.Header>

      <Panel.Content className={cls.driverDetailsPanelContent}>
        <Card className={clsx(panelCls.card, cls.driverDetailsCard)}>
          <Card.Section className={cls.menu}>
            <div className={cls.tabs}>
              <Tabs currentTab={currentTab} setCurrentTab={setCurrentTab}>
                <Tabs.Tab label={t("details")} value="details" />
                <Tabs.Tab label={t("timeline")} value="timeline" />
              </Tabs>
            </div>

            <ContextMenu.Button onClick={openContextMenu} />
          </Card.Section>

          {currentTab === "details" ? (
            <DriverDetails driver={driver} />
          ) : currentTab === "timeline" ? (
            <DriverHistory driver={driver} />
          ) : null}
        </Card>
      </Panel.Content>
    </section>
  )
}
